import React, { useState, useEffect, useMemo } from 'react';
import Fader from 'react-fader';
import { useDispatch } from 'react-redux';
import { Route, useHistory } from 'react-router-dom';
import Switch from 'react-router-transition-switch';

import { ENV } from '@environment';
import * as Sentry from '@sentry/react';
import { AxiosResponse } from 'axios';
import queryString from 'query-string';
import { MyThunkDispatch } from 'store';

import { setOAuthEmail } from 'action-creators/user';
import ConfirmBottomSheet from 'components/ConfirmBottomSheet';
import RegistrationOnboardingLayout from 'components/RegistrationOnboardingLayout';
import { CountryCode } from 'components/Selects/CountryCodeSelect';
import useIsInsideApp from 'hooks/useIsInsideApp';
import useIsMobile from 'hooks/useIsMobile';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { Country as CountryModel } from 'models/country';
import { Response } from 'models/response';
import { RestCountryResponse } from 'models/restCountry';
import * as otpService from 'services/otpService';
import {
  CreateCorporateEmailRequest,
  CreateCorporateEmailResponse,
  SetCountryRequest,
  SetCompanyRequest,
  CreateCompanyRequest,
  LinkRappiUserRequest,
  VerifyLinkedRappiUserParameters,
  VerifyLinkedRappiUserResponse,
} from 'services/registrationService';
import { extractErrorMessage, getCurrentLanguage } from 'utils/generic';

import Administrator, { AdministratorValues } from './forms/Administrator';
import Company, { CompanyValues } from './forms/Company';
import Continue from './forms/Company/Continue';
import Country, { CountryValues } from './forms/Country';
import Email, { EmailValues } from './forms/Email';
import Help from './forms/Help';
import HelpMail from './forms/Help/HelpMail';
import JoinRappi from './forms/JoinRappi';
import RappiAccount from './forms/RappiAccount';
import Telephone, { OTPChannel, TelephoneValues } from './forms/Telephone';
import TwoFactor, { TwoFactorCodeValues, TwoFactorData } from './forms/TwoFactor';
import VerificationCode, { VerificationCodeValues } from './forms/VerificationCode';
import Welcome from './forms/Welcome';
import { BottomSheetTitle } from './styles';

interface Props {
  fetchAvailableCountries: () => Promise<void>;
  fetchCountryCodes: () => Promise<void>;
  sendEmail: (data: CreateCorporateEmailRequest) => Promise<Response<CreateCorporateEmailResponse>>;
  verifyLinkedUser: (data: VerifyLinkedRappiUserParameters) => Promise<Response<VerifyLinkedRappiUserResponse>>;
  sendCountry: (data: SetCountryRequest, id: string) => any;
  sendCompany: (data: SetCompanyRequest, id: string) => any;
  createCompany: (data: CreateCompanyRequest) => Promise<any>;
  linkRappiUser: (data: LinkRappiUserRequest) => Promise<any>;
  loadingCountries: boolean;
  countryList: CountryModel[];
  restCountries: RestCountryResponse[];
  resendTwoFactor: (key: string) => Promise<AxiosResponse<void>>;
}

const KUSTOMER_FORM_ID = ENV.API_KEYS.KUSTOMER_FORM_ID;

const RegistrationOnboarding = ({
  fetchAvailableCountries,
  fetchCountryCodes,
  sendEmail,
  verifyLinkedUser,
  sendCountry,
  sendCompany,
  createCompany,
  linkRappiUser,
  loadingCountries,
  countryList,
  restCountries,
  resendTwoFactor,
}: Props) => {
  const { t } = useStoreTranslation();
  const toast = useToast();
  const history = useHistory();
  const isMobile = useIsMobile();
  const isInsideApp = useIsInsideApp();
  const queryParams = queryString.parse(history.location.search) as { token?: string; email?: string };

  const [leadId, setLeadId] = useState<string | null>(null);
  const [showAppRedirect, setShowAppRedirect] = useState<boolean>(false);
  const [tokenParam, setTokenParam] = useState<string | null>(null);

  const [email, setEmail] = useState<EmailValues | null>(null);
  const [country, setCountry] = useState<CountryValues | null>(null);
  const [company, setCompany] = useState<CompanyValues | null>(null);
  const [administrator, setAdministrator] = useState<AdministratorValues | null>(null);
  const [telephone, setTelephone] = useState<TelephoneValues | null>(null);
  const [otpChannel, setOtpChannel] = useState<OTPChannel>();
  const [verificationCode, setVerificationCode] = useState<VerificationCodeValues | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState<TwoFactorCodeValues | null>(null);
  const [twoFactorData, setTwoFactorData] = useState<TwoFactorData | null>(null);
  const [otpSendResponse, setOtpSendResponse] = useState<
    otpService.CreateWhatsAppOTPResponse | otpService.CreateSMSOTPResponse | null
  >(null);

  const defaultErrorHandler = (error: any) =>
    toast.add({ type: 'danger', message: extractErrorMessage(error) || t('defaultError.default') });

  useEffect(() => {
    try {
      const kustomer = window['Kustomer'];

      if (!kustomer || !kustomer.start) return;

      kustomer.start({ lang: getCurrentLanguage().split('-')[0], form: KUSTOMER_FORM_ID });
      fetchAvailableCountries().catch(defaultErrorHandler);
      fetchCountryCodes().catch(defaultErrorHandler);

      return () => {
        try {
          if (kustomer.stop) kustomer.stop();
        } catch (e) {}
      };
    } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (queryParams.email) {
      history.replace(`/invite${history.location.search}`);
      return;
    }

    if (queryParams.token) {
      setTokenParam(queryParams.token);
    }

    if (!email) history.replace('/corporate/registration');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mapEmailData = (values: EmailValues) => ({
    email: values.email.trim(),
  });
  const mapCountryData = (values: CountryValues) => ({
    countryId: values.country.id,
  });
  const mapCompanyData = (values: CompanyValues) => ({
    companyName: values.name,
    document: values.document,
    issuer: '',
    issuerDate: null,
  });
  const mapAdministratorData = (values: AdministratorValues) => ({
    userName: values.name,
    userLastName: values.lastName,
    userPhoneNumber: `${values.countryCode.code}${values.phoneNumber}`,
  });

  const linkAndLoginUser = async (token: string) => {
    if (!email) return;
    if (!token) return;

    try {
      const linkResult = await linkRappiUser({
        email: email.email.trim(),
        token,
      });
      if (linkResult.errors.some((error) => error.includes('user is already linked'))) {
        toast.add({
          type: 'success',
          message: t('registrationOnboarding.redirectToLinkedAccount'),
        });
      }
    } catch (e) {}

    history.push(`/corporate/?token=${token}`);
  };

  const dispatch: MyThunkDispatch = useDispatch();

  const handleWelcomeSubmit = () => history.push('/corporate/registration/email');
  const handleEmailSubmit = async (values: EmailValues) => {
    setCountry(null);

    dispatch(setOAuthEmail(values.email.trim()));

    const linkResponse = await verifyLinkedUser({ email: values.email.trim() });
    if (linkResponse.data === 'UserNotLinkedWithRappi') {
      setEmail(values);
      setCountry({ country: getDefaultCountry() });
      history.push('/corporate/registration/company/continue');
      return;
    }

    if (values.email.trim() !== email?.email.trim()) {
      try {
        const {
          data: { id },
        } = await sendEmail(mapEmailData(values));

        if (id) setLeadId(id);
      } catch (e) {
        const message =
          e.response.status === 409
            ? t('registrationOnboarding.email.conflictError')
            : extractErrorMessage(e) || t('defaultError.default');

        toast.add({ type: 'danger', message });
        return;
      }
    }

    setEmail(values);
    history.push('/corporate/registration/country');
  };

  const handleCountrySubmit = (values: CountryValues) => {
    if (values.country.id !== country?.country.id) {
      setCountry(values);
      setCompany(null);
      if (leadId) sendCountry(mapCountryData(values), leadId);
    }
    history.push('/corporate/registration/company');
  };
  const handleCompanySubmit = async (values: CompanyValues) => {
    setCompany(values);
    if (!leadId) {
      history.push('/corporate/registration/administrator');
      return;
    }

    try {
      await sendCompany(mapCompanyData(values), leadId);
      history.push('/corporate/registration/administrator');
    } catch (e) {
      if (e.response.status === 409) {
        history.push('/corporate/registration/help');
      } else {
        history.push('/corporate/registration/administrator');
      }
    }
  };
  const handleCompanyContinueSubmit = () => {
    setCompany({} as CompanyValues);
    history.push('/corporate/registration/rappi-account');
  };
  const handleAdministratorSubmit = async (values: AdministratorValues) => {
    if (!email || !country || !company) throw new Error('Invalid form state');

    try {
      await createCompany({
        id: leadId || undefined,
        ...mapEmailData(email),
        ...mapCountryData(country),
        ...mapCompanyData(company),
        ...mapAdministratorData(values),
      });

      if (!isMobile) {
        history.push('/corporate/registration/rappi-account');
      } else if (!isInsideApp) {
        setShowAppRedirect(true);
      } else if (!tokenParam) {
        history.push('/corporate/registration/rappi-account');
      } else {
        try {
          const linkResult = await linkRappiUser({
            email: email.email.trim(),
            token: tokenParam,
          });
          if (linkResult.errors.some((error) => error.includes('user is already linked'))) {
            toast.add({
              type: 'success',
              message: t('registrationOnboarding.redirectToLinkedAccount'),
            });
          }
        } catch (e) {}
        history.push(`/corporate/?token=${tokenParam}`);
      }
    } catch (e) {
      const companyAlreadyExists = e.response?.data?.errors?.some((error: string) =>
        error.toLowerCase().includes('company already exists'),
      );
      if (companyAlreadyExists) {
        history.push('/corporate/registration/help');
      } else {
        defaultErrorHandler(e);
      }
    } finally {
      setAdministrator(values);
    }
  };
  const handleRappiAccountSubmit = (hasAccount: boolean) => {
    if (hasAccount) {
      window.location.href = `${ENV.RAPPI_OAUTH.LOGIN_URL}?client_id=${ENV.RAPPI_OAUTH.LOGIN_CLIENTID}&redirect_uri=${
        ENV.RAPPI_OAUTH.REDIRECT_URL_FROM_REGISTRATION
      }&response_type=code&scope=all&flowName=GeneralOAuthFlow&state=${Math.random()}&subdomain=travel-corporate`;
    } else {
      window.location.href = `${ENV.RAPPI_OAUTH.SIGNUP_URL}?client_id=${ENV.RAPPI_OAUTH.LOGIN_CLIENTID}&redirect_uri=${
        ENV.RAPPI_OAUTH.REDIRECT_URL_FROM_REGISTRATION
      }&response_type=code&scope=all&flowName=GeneralOAuthFlow&state=${Math.random()}&subdomain=travel-corporate`;
    }
  };

  const handleTelephoneSubmit = async (values: TelephoneValues, channel: OTPChannel, redirect = true) => {
    Sentry.addBreadcrumb({ category: 'registration', message: 'Telephone submit', data: { channel, values } });

    if (!channel) return;

    try {
      if (channel === 'whatsapp') {
        setOtpSendResponse(
          await otpService.sendWhatsAppOTP({
            country_code: values.country_code.code,
            phone: values.phone,
          }),
        );
      } else if (channel === 'sms') {
        setOtpSendResponse(
          await otpService.sendSMSOTP({
            country_code: values.country_code.code,
            phone: values.phone,
            locale: t('locale').substring(0, 2),
            via: 'sms',
          }),
        );
      }
      if (redirect) history.push('/corporate/registration/verification-code');
    } catch (e) {
      const errorCode = e.response?.data?.error?.code;

      if (errorCode) {
        toast.add({
          type: 'danger',
          message: t([`registrationOnboarding.error.${errorCode}` || e.response.data.error.message]),
        });
      } else {
        defaultErrorHandler(e);
      }
    } finally {
      setTelephone(values);
      setOtpChannel(channel);
    }
  };
  const handleResendCode = () => {
    if (!telephone || !otpChannel) return;
    return handleTelephoneSubmit(telephone, otpChannel, false);
  };
  const handleVerificationCodeSubmit = async (values: VerificationCodeValues) => {
    Sentry.addBreadcrumb({
      category: 'registration',
      message: 'Verification code submit',
      data: { otpChannel, values },
    });

    if (!telephone || !otpSendResponse || !otpChannel) return;

    const phone = telephone.phone;
    const country_code = telephone.country_code.code;

    try {
      let otpValidateResponse;
      if (otpChannel === 'whatsapp') {
        otpValidateResponse = await otpService.validateWhatsAppOTP({
          country_code,
          phone,
          code: values.code,
        });
      } else if (otpChannel === 'sms') {
        otpValidateResponse = await otpService.validateSMSOTP({
          country_code,
          phone,
          code: values.code,
          uuid: (otpSendResponse as otpService.CreateSMSOTPResponse).sid,
        });
      }

      if (!otpValidateResponse.success && !otpValidateResponse.valid) {
        Sentry.addBreadcrumb({
          category: 'registration',
          message: 'OTP validate response fail',
          data: otpValidateResponse,
        });

        Sentry.withScope(function (scope) {
          scope.setTag('email', email?.email ?? '');
          scope.setTag('country_code', country_code ?? '');
          scope.setTag('phone', phone ?? '');
          scope.setTag('channel', otpChannel ?? '');
          scope.setTag('company_name', company?.name ?? '');
          scope.setTag('company_code', company?.document ?? '');

          Sentry.captureMessage('[RegistrationOnboarding] OTP validate response fail');
        });

        toast.add({ type: 'danger', message: t('registrationOnboarding.error.invalid_code') });
        return;
      }

      let otpAuthenticateResponse;
      if (otpChannel === 'whatsapp') {
        otpAuthenticateResponse = await otpService.authenticateWhatsAppOTP({
          accessToken: (otpSendResponse as otpService.CreateWhatsAppOTPResponse).uuid,
          email: '',
          first_name: '',
          last_name: '',
          name: '',
          gender: '',
          replacement_criteria_id: 'replace',
          scope: 'all',
          social_id: phone,
          global_phone_number: `${country_code}${phone}`,
          sms_code: '',
          validate_code: false,
          phone,
          country_code,
        });
      } else if (otpChannel === 'sms') {
        otpAuthenticateResponse = await otpService.authenticateSMSOTP({
          accessToken: (otpSendResponse as otpService.CreateSMSOTPResponse).sid,
          country_code,
          phone,
          scope: 'all',
          social_id: phone,
        });
      }

      Sentry.addBreadcrumb({
        category: 'registration',
        message: 'OTP authenticate response',
        data: otpAuthenticateResponse,
      });

      await linkAndLoginUser(otpAuthenticateResponse.access_token);
    } catch (e) {
      const status = e.response?.status;
      const errors = e.response?.data?.errors;
      const error = e.response?.data?.error;

      if (status === 400) {
        if (errors?.name || errors?.nombre || errors?.email) {
          Sentry.addBreadcrumb({
            category: 'registration',
            message: "OTP responded that the Rappi user doesn't exist",
          });
          history.push('/corporate/registration/join-rappi');
        } else if (error.code === 'verification_code_required') {
          Sentry.addBreadcrumb({ category: 'registration', message: '2FA required for this user', data: error });
          setTwoFactorData(error);
          history.push('/corporate/registration/two-factor');
        } else {
          defaultErrorHandler(e);
        }
      } else {
        defaultErrorHandler(e);
      }

      Sentry.withScope(function (scope) {
        scope.setTag('email', email?.email ?? '');
        scope.setTag('country_code', country_code ?? '');
        scope.setTag('phone', phone ?? '');
        scope.setTag('channel', otpChannel ?? '');
        scope.setTag('company_name', company?.name ?? '');
        scope.setTag('company_code', company?.document ?? '');

        Sentry.captureMessage('[Registration] Error on verification code submit');
      });
    } finally {
      setVerificationCode(values);
    }
  };
  const handleResendTwoFactorCode = async () => {
    if (!twoFactorData) return;
    try {
      await resendTwoFactor(twoFactorData.key);
    } catch (e) {
      const errorCode = e.response?.data?.error?.code;

      Sentry.addBreadcrumb({
        category: 'registration',
        message: '2FA code resend failed',
        data: { code: errorCode, response: e.response },
      });

      if (errorCode) {
        toast.add({
          type: 'danger',
          message: t([`registrationOnboarding.error.${errorCode}` || e.response.data.error.message]),
        });
        history.replace('/registration/telephone');
      } else {
        defaultErrorHandler(e);
      }

      Sentry.withScope(function (scope) {
        scope.setTag('email', email?.email ?? '');
        scope.setTag('channel', otpChannel ?? '');

        Sentry.captureMessage('[Registration] Failed to resend 2FA code');
      });
    }
  };
  const handleTwoFactorCodeSubmit = async (values: TwoFactorCodeValues) => {
    Sentry.addBreadcrumb({ category: 'registration', message: 'Two factor code submit', data: values });

    if (!telephone || !twoFactorData || !otpSendResponse || !otpChannel) return;

    const phone = telephone.phone;
    const country_code = telephone.country_code.code;

    try {
      const otpAuth = {
        'x-two-factor-auth': twoFactorData.key,
        'x-two-factor-code': values.code,
      };

      let otpAuthenticateResponse;
      if (otpChannel === 'whatsapp') {
        otpAuthenticateResponse = await otpService.authenticateWhatsAppOTP(
          {
            accessToken: (otpSendResponse as otpService.CreateWhatsAppOTPResponse).uuid,
            email: '',
            first_name: '',
            last_name: '',
            name: '',
            gender: '',
            replacement_criteria_id: 'replace',
            scope: 'all',
            social_id: phone,
            global_phone_number: `${country_code}${phone}`,
            sms_code: '',
            validate_code: false,
            phone,
            country_code,
          },
          otpAuth,
        );
      } else if (otpChannel === 'sms') {
        otpAuthenticateResponse = await otpService.authenticateSMSOTP(
          {
            accessToken: (otpSendResponse as otpService.CreateSMSOTPResponse).sid,
            country_code,
            phone,
            scope: 'all',
            social_id: phone,
          },
          otpAuth,
        );
      }

      Sentry.addBreadcrumb({
        category: 'registration',
        message: 'Two factor OTP authenticate response',
        data: otpAuthenticateResponse,
      });

      await linkAndLoginUser(otpAuthenticateResponse.access_token);
    } catch (e) {
      const error = e.response?.data?.error;
      const status = e.response?.status;

      if (status === 400 && error?.code === 'invalid_code') {
        Sentry.captureMessage('[RegistrationOnboarding] 2FA code is invalid');
        toast.add({ type: 'danger', message: t('registrationOnboarding.error.invalid_code') });
        return;
      }

      defaultErrorHandler(e);
      Sentry.captureMessage('[Registration] Error on 2FA verification code submit');
    } finally {
      setTwoFactorCode(values);
    }
  };

  const countryCodes: CountryCode[] = useMemo(
    () =>
      restCountries
        .filter((c) => c.callingCodes.length > 0 && c.callingCodes[0])
        .map((c) => ({
          id: c.alpha2Code,
          name: c.name,
          flag: c.flag,
          code: `+${c.callingCodes[0]}`,
        }))
        .sort((a, b) => (Number(a.code.replace(/[\D]/g, '')) > Number(b.code.replace(/[\D]/g, '')) ? 1 : -1)),
    [restCountries],
  );

  const getDefaultCountry = () => {
    const { hostname } = window.location;

    if (hostname.endsWith('uy'))
      return { id: 'UY', name: 'Uruguai', culture: 'es-UY', currency: 'UYU', documentType: '' };
    if (hostname.endsWith('ar'))
      return { id: 'AR', name: 'Argentina', culture: 'es-AR', currency: 'ARS', documentType: '' };
    if (hostname.endsWith('br'))
      return { id: 'BR', name: 'Brasil', culture: 'pt-BR', currency: 'BRL', documentType: '' };
    if (hostname.endsWith('mx'))
      return { id: 'MX', name: 'Mexico', culture: 'es-MX', currency: 'MXN', documentType: '' };

    return { id: 'CO', name: 'Colombia', culture: 'es-CO', currency: 'COP', documentType: '' };
  };

  const current = history.location.pathname.split('/').last();
  const getSideAssetUrl = () => {
    if (current === 'registration') return '/corporate/images/registration-onboarding/plane.svg';
    if (current === 'rappi-account') return '/corporate/images/registration-onboarding/rocket.svg';
    if (current === 'join-rappi') return '/corporate/images/logo-rappi-oficial-white.svg';
    return undefined;
  };
  const getCurrentStep = () => {
    switch (current) {
      case 'email':
        return 0;
      case 'country':
        return 1;
      case 'company':
      case 'non-nit-company':
        return 2;
      case 'administrator':
        return 3;
      default:
        return undefined;
    }
  };
  const getAllowBack = () => {
    switch (current) {
      case 'registration':
      case 'rappi-account':
        return false;
      default:
        return true;
    }
  };

  return (
    <>
      <RegistrationOnboardingLayout
        appBarTitle={t('registrationOnboarding.mobileTitle')}
        current={current}
        sideAssetUrl={getSideAssetUrl()}
        totalSteps={4}
        currentStep={getCurrentStep()}
        noGoBack={!getAllowBack()}
      >
        <Switch
          render={({ children }: any) => (
            <Fader style={isMobile ? { height: '100%' } : {}} viewStyle={isMobile ? { height: '100%' } : {}}>
              {children}
            </Fader>
          )}
        >
          <Route exact path="/corporate/registration" component={() => <Welcome onSubmit={handleWelcomeSubmit} />} />
          <Route
            path="/corporate/registration/email"
            component={() => <Email values={email} onSubmit={handleEmailSubmit} />}
          />
          <Route exact path="/corporate/registration/help" component={() => <Help />} />
          <Route exact path="/corporate/registration/help/email" component={() => <HelpMail email={email} />} />
          <Route
            exact
            path="/corporate/registration/country"
            component={() => (
              <Country
                values={country}
                onSubmit={handleCountrySubmit}
                loadingCountries={loadingCountries}
                countryList={countryList}
              />
            )}
          />
          <Route
            exact
            path="/corporate/registration/company"
            component={() => <Company values={company} onSubmit={handleCompanySubmit} country={country} />}
          />
          <Route
            exact
            path="/corporate/registration/company/continue"
            component={() => <Continue company={company} onSubmit={handleCompanyContinueSubmit} country={country} />}
          />
          <Route
            exact
            path="/corporate/registration/administrator"
            component={() => (
              <Administrator
                values={administrator}
                onSubmit={handleAdministratorSubmit}
                countryCodes={countryCodes}
                country={country}
              />
            )}
          />
          <Route
            exact
            path="/corporate/registration/rappi-account"
            component={() => (
              <RappiAccount onSubmit={handleRappiAccountSubmit} registrationEmail={email?.email ?? ''} />
            )}
          />
          <Route
            exact
            path="/corporate/registration/telephone"
            component={() => (
              <Telephone
                values={telephone}
                onSubmit={handleTelephoneSubmit}
                countryCodes={countryCodes}
                country={country}
              />
            )}
          />
          <Route
            exact
            path="/corporate/registration/telephone/create"
            component={() => (
              <Telephone
                variant="register-number"
                values={telephone}
                onSubmit={handleTelephoneSubmit}
                countryCodes={countryCodes}
                country={country}
              />
            )}
          />
          <Route
            exact
            path="/corporate/registration/telephone/incorrect-phone"
            component={() => (
              <Telephone
                variant="incorrect-number"
                values={telephone}
                onSubmit={handleTelephoneSubmit}
                countryCodes={countryCodes}
                country={country}
              />
            )}
          />
          <Route
            exact
            path="/corporate/registration/join-rappi"
            component={() => <JoinRappi telephone={telephone} email={email} />}
          />
          <Route
            exact
            path="/corporate/registration/verification-code"
            component={() => (
              <VerificationCode
                values={verificationCode}
                onSubmit={handleVerificationCodeSubmit}
                onResend={handleResendCode}
                telephone={telephone}
                channel={otpChannel}
              />
            )}
          />
          <Route
            exact
            path="/corporate/registration/two-factor"
            component={() => (
              <TwoFactor
                values={twoFactorCode}
                onSubmit={handleTwoFactorCodeSubmit}
                onResend={handleResendTwoFactorCode}
                data={twoFactorData}
                telephone={telephone}
              />
            )}
          />
        </Switch>
      </RegistrationOnboardingLayout>

      <ConfirmBottomSheet
        red
        show={showAppRedirect}
        content={<BottomSheetTitle>{t('registrationOnboarding.appRedirect.title')}</BottomSheetTitle>}
        confirmText={t('registrationOnboarding.appRedirect.confirm')}
        onConfirm={() => {
          const { origin } = window.location;
          const deepLink = `https://rappi.onelink.me/2373836517?pid=Email&af_ios_url=${origin}/&af_android_url=${origin}/&af_dp=gbrappi://com.grability.rappi?store_type=travel_corp_uy&path=dashboard&email=${email?.email}&af_web_dp=${origin}&af_force_deeplink=true`;
          window.location.href = deepLink;
        }}
      />
    </>
  );
};

export default RegistrationOnboarding;
